/**
 * FACADE LINES (2026-10-10, Matt: "Right now the system just feels messy … not all walls are
 * modelled as perfect monoliths, especially when there are two or more stories and we have
 * single and double height next to each other, or walls that partially extend up to a lower
 * roof and then partially to a higher"; docs/bim-plan/34-finish-studio.md §4). A finish line
 * drawn ON THE EXTERIOR, across as many walls as it touches — not owned by any one wall.
 *
 * Model: ONE record per line in the settings map (`facadeLines`): its height (a level + an
 * offset, so it follows the level), the walls it crosses, and the finish painted above and
 * below it. Each crossed wall carries a MATERIALIZED copy of the split on its own
 * `faceSplits` (id `fl_<lineId>`, `lineId` set), so every existing consumer — the 3D skin
 * bands, band thickness steps, siding/brick/stone layout, the elevation lines, the Paint
 * Bucket's band hit — works unchanged, and the worker rebuilds exactly the walls a line
 * touches. The copy's height and paint are re-derived from the record at resolve
 * (`resolveFacadeSplits`), so a level move, a copied wall or an undo can never leave a
 * stale or orphaned split behind.
 *
 * Step one: lines are level and run whole walls; ending partway along a wall and vertical /
 * stepped lines are the next step (they need bands bounded along the wall, not just in z).
 */
import type * as Y from 'yjs'
import { maps, transact, getElement, getLevel, listElements, resolveElement, type ModelDoc } from './doc'
import type { ElementId, FaceSplitEntry, LevelId } from './types'
import type { ResolvedElement } from './params'

export interface FacadeLine {
  id: string
  /** the height: `offsetFt` above this level's elevation */
  levelId: string
  offsetFt: number
  /** the walls (exterior faces) the line crosses, in drawing order */
  walls: string[]
  /** the finish painted on the band above / below the line (null = the face's own) */
  above?: string | null
  below?: string | null
  aboveColor?: string | null
  belowColor?: string | null
}

const KEY = 'facadeLines'
export const FACADE_SPLIT_PREFIX = 'fl_'
const MIN_GAP_FT = 1 / 96

export const facadeSplitId = (lineId: string) => FACADE_SPLIT_PREFIX + lineId
export const lineIdOfSplit = (splitId: string | null | undefined): string | null =>
  splitId && splitId.startsWith(FACADE_SPLIT_PREFIX) ? splitId.slice(FACADE_SPLIT_PREFIX.length) : null

function readAll(model: ModelDoc): Record<string, FacadeLine> {
  const v = maps(model).settings.get(KEY) as Record<string, FacadeLine> | undefined
  return v && typeof v === 'object' ? v : {}
}

export function listFacadeLines(model: ModelDoc): FacadeLine[] {
  return Object.values(readAll(model)).filter((l) => l && typeof l.id === 'string' && Array.isArray(l.walls))
}

export function getFacadeLine(model: ModelDoc, id: string): FacadeLine | null {
  const l = readAll(model)[id]
  return l && Array.isArray(l.walls) ? l : null
}

/** the line's height in world feet, or null when its level is gone */
export function facadeLineZ(model: ModelDoc, line: FacadeLine): number | null {
  const lv = getLevel(model, line.levelId as LevelId)
  return lv ? lv.elevationFt + (Number(line.offsetFt) || 0) : null
}

/** a wall's base and top in world feet (generate3d's wallZRange, from the resolved wall) */
export function wallZSpan(el: ResolvedElement): { z0: number; z1: number } | null {
  if (!el.level) return null
  const z0 = el.level.elevationFt + ((el.params.baseOffsetFt as number) || 0)
  const topLevel = el.params.topMode === 'level' ? el.paramLevels?.topLevelId : undefined
  const z1 = topLevel
    ? topLevel.elevationFt + ((el.params.topOffsetFt as number) || 0)
    : z0 + ((el.params.unconnectedHeightFt as number) || 9)
  return z1 - z0 > 1e-6 ? { z0, z1 } : null
}

/**
 * Resolve-time (doc.ts): a wall's own split list with every facade-line copy re-derived
 * from its record — height from the line's level, paint from the line — and dropped when
 * the line is gone, no longer lists this wall, or passes above/below the wall.
 */
export function resolveFacadeSplits(
  model: ModelDoc, wallId: string, splits: FaceSplitEntry[], zSpan: { z0: number; z1: number } | null,
): FaceSplitEntry[] {
  if (!splits.some((s) => s.lineId)) return splits
  const out: FaceSplitEntry[] = []
  for (const s of splits) {
    if (!s.lineId) { out.push(s); continue }
    const line = getFacadeLine(model, s.lineId)
    if (!line || !line.walls.includes(wallId) || !zSpan) continue
    const z = facadeLineZ(model, line)
    if (z == null) continue
    const zFt = z - zSpan.z0
    if (zFt <= MIN_GAP_FT || zFt >= zSpan.z1 - zSpan.z0 - MIN_GAP_FT) continue
    out.push({
      ...s, side: 'exterior', zFt,
      above: line.above ?? null, below: line.below ?? null,
      aboveColor: line.aboveColor ?? null, belowColor: line.belowColor ?? null,
    })
  }
  return out
}

// ---------------------------------------------------------------- the facade walk

type WallInfo = { id: string; a: [number, number]; b: [number, number]; len: number; z0: number; z1: number; exterior: boolean }

function wallInfos(model: ModelDoc): WallInfo[] {
  const out: WallInfo[] = []
  for (const r of listElements(model)) {
    if (r.category !== 'wall') continue
    const el = resolveElement(model, r.id)
    const nodes = (el?.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes
    const zs = el ? wallZSpan(el) : null
    if (!el || !nodes || nodes.length < 2 || !zs) continue
    const pts = nodes.map((n) => n.pt)
    let len = 0
    for (let i = 0; i + 1 < pts.length; i++) len += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
    out.push({
      id: r.id, a: pts[0], b: pts[pts.length - 1], len, z0: zs.z0, z1: zs.z1,
      exterior: el.params.function !== 'interior',
    })
  }
  return out
}

/** ends closer than this join (plan feet — corner centerlines meet within a wall thickness) */
const JOIN_TOL_FT = 1.25

/**
 * The walls a level line at world height `z` crosses going from `fromWall` to `toWall`
 * along the building's exterior: the shortest chain (by wall length) of exterior walls
 * joined end to end whose height span includes `z`. Stacked stories and single beside
 * double height fall out on their own — a wall the height misses is simply not on the
 * chain, and the walls above or beside it that do reach it are. [fromWall] alone when
 * the two are the same wall; null when no chain reaches.
 */
export function facadeChain(model: ModelDoc, fromWall: string, toWall: string, z: number): string[] | null {
  if (fromWall === toWall) return [fromWall]
  const all = wallInfos(model)
  const at = (w: WallInfo) => z > w.z0 + MIN_GAP_FT && z < w.z1 - MIN_GAP_FT
  const from = all.find((w) => w.id === fromWall), to = all.find((w) => w.id === toWall)
  if (!from || !to) return null
  const pool = all.filter((w) => w.id === fromWall || w.id === toWall || (w.exterior && at(w)))
  const near = (p: [number, number], q: [number, number]) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= JOIN_TOL_FT
  const joined = (u: WallInfo, v: WallInfo) => near(u.a, v.a) || near(u.a, v.b) || near(u.b, v.a) || near(u.b, v.b)
  // Dijkstra over walls, cost = the length of the walls walked
  const dist = new Map<string, number>([[from.id, 0]])
  const prev = new Map<string, string>()
  const done = new Set<string>()
  for (;;) {
    let cur: WallInfo | null = null, best = Infinity
    for (const w of pool) {
      const d = dist.get(w.id)
      if (d != null && !done.has(w.id) && d < best) { best = d; cur = w }
    }
    if (!cur) return null
    if (cur.id === to.id) break
    done.add(cur.id)
    for (const w of pool) {
      if (done.has(w.id) || !joined(cur, w)) continue
      const d = best + w.len
      if (d < (dist.get(w.id) ?? Infinity)) { dist.set(w.id, d); prev.set(w.id, cur.id) }
    }
  }
  const chain = [to.id]
  while (chain[0] !== from.id) chain.unshift(prev.get(chain[0])!)
  return chain
}

// ---------------------------------------------------------------- writes

const newId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36)

function writeLine(model: ModelDoc, line: FacadeLine | null, id: string): void {
  const all = { ...readAll(model) }
  if (line) all[id] = line
  else delete all[id]
  maps(model).settings.set(KEY, all)
}

function wallParams(model: ModelDoc, wallId: string): Y.Map<unknown> | null {
  const em = maps(model).elements.get(wallId)
  return em ? (em.get('params') as Y.Map<unknown>) : null
}

/** rewrite every wall's materialized copy of `lineId` to match the record (null = removed) */
function syncWalls(model: ModelDoc, lineId: string, line: FacadeLine | null): void {
  const sid = facadeSplitId(lineId)
  const want = new Set(line ? line.walls : [])
  for (const r of listElements(model)) {
    if (r.category !== 'wall') continue
    const cur = Array.isArray(r.params?.faceSplits) ? (r.params.faceSplits as FaceSplitEntry[]) : []
    const has = cur.some((s) => s.id === sid)
    if (!want.has(r.id) && !has) continue
    const pm = wallParams(model, r.id)
    if (!pm) continue
    const rest = cur.filter((s) => s.id !== sid)
    if (!want.has(r.id) || !line) {
      pm.set('faceSplits', rest.length ? rest : null)
      continue
    }
    // the stored copy carries the paint too, so the wall's own params read sensibly; the
    // height is re-derived at resolve, but stored here so a reader without a model
    // handle sees a reasonable value
    const el = resolveElement(model, r.id as ElementId)
    const zs = el ? wallZSpan(el) : null
    const z = facadeLineZ(model, line)
    const entry: FaceSplitEntry = {
      id: sid, lineId, side: 'exterior', zFt: zs && z != null ? z - zs.z0 : 0,
      above: line.above ?? null, below: line.below ?? null,
      aboveColor: line.aboveColor ?? null, belowColor: line.belowColor ?? null,
    }
    pm.set('faceSplits', [...rest, entry])
  }
}

/** a new line at `offsetFt` above `levelId` across `walls` -> its id (null = refused) */
export function addFacadeLine(model: ModelDoc, init: {
  levelId: string; offsetFt: number; walls: string[]
  above?: string | null; below?: string | null; aboveColor?: string | null; belowColor?: string | null
}): string | null {
  if (!getLevel(model, init.levelId as LevelId) || !Number.isFinite(init.offsetFt)) return null
  const walls = [...new Set(init.walls)].filter((w) => getElement(model, w as ElementId)?.category === 'wall')
  if (!walls.length) return null
  const id = newId()
  const line: FacadeLine = {
    id, levelId: init.levelId, offsetFt: init.offsetFt, walls,
    above: init.above ?? null, below: init.below ?? null,
    aboveColor: init.aboveColor ?? null, belowColor: init.belowColor ?? null,
  }
  transact(model, () => { writeLine(model, line, id); syncWalls(model, id, line) })
  return id
}

function update(model: ModelDoc, id: string, patch: Partial<FacadeLine>): boolean {
  const cur = getFacadeLine(model, id)
  if (!cur) return false
  const line: FacadeLine = { ...cur, ...patch, id }
  transact(model, () => { writeLine(model, line, id); syncWalls(model, id, line) })
  return true
}

/** move the line to world height `z` (kept on its own level) */
export function setFacadeLineZ(model: ModelDoc, id: string, z: number): boolean {
  const cur = getFacadeLine(model, id)
  const lv = cur ? getLevel(model, cur.levelId as LevelId) : null
  if (!cur || !lv || !Number.isFinite(z)) return false
  return update(model, id, { offsetFt: z - lv.elevationFt })
}

/** the walls the line crosses (an empty list removes the line) */
export function setFacadeLineWalls(model: ModelDoc, id: string, walls: string[]): boolean {
  const list = [...new Set(walls)]
  if (!list.length) return removeFacadeLine(model, id)
  return update(model, id, { walls: list })
}

/** add the wall if the line doesn't cross it, else take it off -> the line still exists */
export function toggleFacadeLineWall(model: ModelDoc, id: string, wallId: string): boolean {
  const cur = getFacadeLine(model, id)
  if (!cur) return false
  const walls = cur.walls.includes(wallId) ? cur.walls.filter((w) => w !== wallId) : [...cur.walls, wallId]
  setFacadeLineWalls(model, id, walls)
  return !!getFacadeLine(model, id)
}

export function removeFacadeLine(model: ModelDoc, id: string): boolean {
  if (!getFacadeLine(model, id)) return false
  transact(model, () => { writeLine(model, null, id); syncWalls(model, id, null) })
  return true
}

/**
 * Paint the band above or below the line, on EVERY wall it crosses. Painting below also
 * clears a wall's own bottom-band paint where this line is that wall's lowest split, so
 * "below this line" means the same thing on every wall. -> changed
 */
export function paintFacadeLine(
  model: ModelDoc, id: string, which: 'above' | 'below', materialId: string | null,
  baseKey: string,
): boolean {
  const cur = getFacadeLine(model, id)
  if (!cur) return false
  const mat = materialId || null
  if ((cur[which] ?? null) === mat) return false
  transact(model, () => {
    update(model, id, { [which]: mat } as Partial<FacadeLine>)
    if (which === 'below') clearOwnBase(model, cur, baseKey)
  })
  return true
}

export function paintFacadeLineColor(
  model: ModelDoc, id: string, which: 'above' | 'below', colorId: string | null,
  baseColorKey: string,
): boolean {
  const cur = getFacadeLine(model, id)
  if (!cur) return false
  const col = colorId || null
  const key = which === 'above' ? 'aboveColor' : 'belowColor'
  if ((cur[key] ?? null) === col) return false
  transact(model, () => {
    update(model, id, { [key]: col } as Partial<FacadeLine>)
    if (which === 'below') clearOwnBase(model, cur, baseColorKey)
  })
  return true
}

/** clear `key` (a wall's own bottom-band paint/color) on every wall whose lowest split is this line */
function clearOwnBase(model: ModelDoc, line: FacadeLine, key: string): void {
  const sid = facadeSplitId(line.id)
  for (const w of line.walls) {
    const el = resolveElement(model, w as ElementId)
    const splits = (Array.isArray(el?.params.faceSplits) ? el!.params.faceSplits as FaceSplitEntry[] : [])
      .filter((s) => s.side === 'exterior').sort((a, b) => a.zFt - b.zFt)
    if (splits[0]?.id !== sid) continue
    const pm = wallParams(model, w)
    if (pm && pm.get(key) != null) pm.set(key, null)
  }
}
